import { useState } from "react";

import { getAllSettledPromiseValue } from "shared/utils/getAllSettledPromiseValue";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";

import {
  getAllAddress,
  getAllCreditCards,
  getUserSettings,
} from "@payment/g/[uuid]/utils/requests";
import { captureException, captureMessage } from "@sentry/nextjs";

interface IUserSettings {
  display_name: string;
  document: string;
  document_type: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  type: string;
  type_entity: string;
}

interface IUserCheckoutInfos {
  addressList: any[];
  userSettings: IUserSettings;
  creditCardList: any[];
}

export const useUserCheckoutAutocompleteInfos = () => {
  const { loggedUser } = useAuthContext();
  const [userCheckoutInfos, setUserCheckouInfos] = useState<IUserCheckoutInfos>(
    {
      addressList: [],
      userSettings: {} as IUserSettings,
      creditCardList: [],
    },
  );

  const loadUserSettings = async (): Promise<void> => {
    try {
      const [settingsResponse] = await Promise.allSettled([getUserSettings()]);

      const settingsResult = getAllSettledPromiseValue(settingsResponse, []);

      setUserCheckouInfos((prev) => ({
        ...prev,
        userSettings: settingsResult,
      }));
    } catch (error) {
      console.log("Erro (loadUserSettings): ", error);
      captureException(error);
      captureMessage("Erro (loadUserSettings)");
    }
  };

  const loadUserCreditCardList = async (): Promise<void> => {
    try {
      const [creditCardListResponse] = await Promise.allSettled([
        getAllCreditCards(),
      ]);

      const creditCardListResult = getAllSettledPromiseValue(
        creditCardListResponse,
        [],
      );

      setUserCheckouInfos((prev) => ({
        ...prev,
        creditCardList: creditCardListResult,
      }));
    } catch (error) {
      console.log("Erro (loadUserCreditCardList): ", error);
      captureException(error);
      captureMessage("Erro (loadUserCreditCardList)");
    }
  };

  const loadUserAddressList = async (): Promise<void> => {
    try {
      const [addressListResponse] = await Promise.allSettled([getAllAddress()]);

      const addressListResult = getAllSettledPromiseValue(
        addressListResponse,
        [],
      );

      setUserCheckouInfos((prev) => ({
        ...prev,
        addressList: addressListResult,
      }));
    } catch (error) {
      console.log("Erro (loadUserAddressList): ", error);
      captureException(error);
      captureMessage("Erro (loadUserAddressList)");
    }
  };

  const loadUserCheckoutInfos = async (): Promise<void> => {
    try {
      if (!loggedUser) return;

      await Promise.all([
        [loadUserSettings(), loadUserCreditCardList(), loadUserAddressList()],
      ]);
    } catch (error) {
      console.log("Erro (loadUserCheckoutInfos): ", error);
      captureException(error);
      captureMessage("Erro (loadUserCheckoutInfos)");
    }
  };


  return {
    userCheckoutInfos,
    loadUserCheckoutInfos,
  };
};
